import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './auth/context.js';
import { LoginPage } from './pages/LoginPage.js';
import { InvitationPage } from './pages/InvitationPage.js';
import { BoardPage } from './pages/BoardPage.js';
import { HistoriquePage } from './pages/HistoriquePage.js';
import { AdminMenuPage } from './pages/AdminMenuPage.js';
import { TablesPage } from './pages/TablesPage.js';
import { DailyReportPage } from './pages/DailyReportPage.js';
import { TeamPage } from './pages/TeamPage.js';
import { BalancePage } from './pages/BalancePage.js';
import { ProfilePage } from './pages/ProfilePage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { OfflineBanner } from './components/OfflineBanner.js';
import { AdminLayout } from './components/AdminLayout.js';
import { EmptyState } from './components/EmptyState.js';
import { Logo } from './components/Logo.js';
import { VITRINE_URL } from './lib/vitrine.js';
import { labels } from './labels.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 15_000 },
  },
});

/** Brand splash while the session is restored: never a blank page. */
function Splash() {
  return (
    <main className="empty-state" style={{ minHeight: '100dvh', justifyContent: 'center' }} aria-busy="true">
      <Logo size={48} />
      <p className="empty-state__text">{labels.auth.restoring}</p>
    </main>
  );
}

/**
 * A visitor who is not signed in sees the landing page first, then signs in
 * from it. Without a landing page URL, the sign-in screen is shown directly.
 */
function ToVitrine() {
  useEffect(() => {
    if (VITRINE_URL) window.location.replace(VITRINE_URL);
  }, []);
  if (!VITRINE_URL) return <Navigate to="/connexion" replace />;
  return <Splash />;
}

function AuthGuard() {
  const { auth, loading } = useAuth();
  if (loading) return <Splash />;
  if (!auth) return <ToVitrine />;
  return <AdminLayout />;
}

function RoleGuard({ minimum }: { minimum: 'MANAGER' | 'OWNER' | 'PLATFORM_ADMIN' }) {
  const { auth } = useAuth();
  if (!auth) return null;
  const rank = { PLATFORM_ADMIN: 4, OWNER: 3, MANAGER: 2, STAFF: 1 }[auth.user.role] ?? 0;
  const minRank = { MANAGER: 2, OWNER: 3, PLATFORM_ADMIN: 4 }[minimum] ?? 0;
  if (rank < minRank) {
    return (
      <div className="page">
        <EmptyState icon="lock" title={labels.errors.forbiddenTitle} text={labels.errors.forbidden} />
      </div>
    );
  }
  return <Outlet />;
}

/**
 * Le kanban, ou un ecran de choix.
 *
 * Un administrateur de plateforme n'est rattache a aucun restaurant (ADR 0009).
 * Tant qu'il n'a bascule sur aucun, le kanban lui repondrait FORBIDDEN : on lui
 * montre donc de quoi choisir, non une erreur. L'ouverture des restaurants,
 * elle, a sa propre application. Voir docs/adr/0015.
 */
function HomeRoute() {
  // Un compte de plateforme n'entre plus ici : le serveur le refuse a la porte
  // (ADR 0030). Il n'y a donc plus d'etat intermediaire a afficher.
  return <BoardPage />;
}

function LoginOrRedirect() {
  const { auth, loading } = useAuth();
  if (loading) return <Splash />;
  if (auth) return <Navigate to="/" replace />;
  return <LoginPage />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <OfflineBanner />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/connexion" element={<LoginOrRedirect />} />
            {/* Ouvert sans session : le gerant qui arrive ici n'a pas encore de
                mot de passe. Voir docs/adr/0024. */}
            <Route path="/invitation/:token" element={<InvitationPage />} />
            <Route element={<AuthGuard />}>
              <Route path="/" element={<HomeRoute />} />
              <Route path="/historique" element={<HistoriquePage />} />
              <Route path="/profil" element={<ProfilePage />} />
              <Route path="/menu" element={<AdminMenuPage />} />
              <Route path="/tables" element={<TablesPage />} />
              <Route element={<RoleGuard minimum="MANAGER" />}>
                <Route path="/bilan" element={<DailyReportPage />} />
              </Route>
              <Route element={<RoleGuard minimum="OWNER" />}>
                <Route path="/equipe" element={<TeamPage />} />
                <Route path="/solde" element={<BalancePage />} />
              </Route>
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
